import React, {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	PublicClientApplication,
	InteractionRequiredAuthError,
	SilentRequest,
	PopupRequest,
} from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';
import { IUserProps } from '../common/data/userDummyData';
import { UserType } from '../common/data/dummyRolesData';
import authService, { IUserProfile } from '../services/api/authService';
// import organizationService from '../services/api/organizationService';
import profileService from '../services/api/profileService';
import msalConfig, { apiScopes } from '../config/msalConfig';

import UserImage from '../assets/img/wanna/wanna1.png';
import UserImageWebp from '../assets/img/wanna/wanna1.webp';
import { isRichHtmlEmpty } from '../helpers/helpers';

const msalInstance = new PublicClientApplication(msalConfig);

// === Context types ===
export interface IAuthContextProps {
	userData: Partial<IUserProps>;
	isAuthenticated: boolean;
	isInitialized: boolean;
	isLoading: boolean;
	isBootstrapped: boolean;
	login: () => Promise<void>;
	logout: () => void;
	acquireToken: () => Promise<string | null>;

	// Backward compatibility
	userToken?: string;
	setUserToken?: (token: string) => void;
	setUserData?: (userData: Partial<IUserProps>) => void;
	syncUserData?: (force?: boolean) => Promise<any>;
}

const initialAuthContext: IAuthContextProps = {
	userData: {},
	isAuthenticated: false,
	isInitialized: false,
	isLoading: true,
	isBootstrapped: false,
	login: async () => {},
	logout: () => {},
	acquireToken: async () => null,
};

const AuthContext = createContext<IAuthContextProps>(initialAuthContext);

interface IAuthContextProviderProps {
	children: ReactNode;
}

export const AuthContextProvider: FC<IAuthContextProviderProps> = ({ children }) => {
	const navigate = useNavigate();

	// ----- State -----
	const [userData, setUserData] = useState<Partial<IUserProps>>({});
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isBootstrapped, setIsBootstrapped] = useState(false);
	const [userToken, setUserTokenState] = useState('');

	// ----- Refs -----
	const initOnceRef = useRef(false);
	const bootOnceRef = useRef(false);
	const isRefreshingRef = useRef(false);

	const syncPromiseRef = useRef<Promise<any> | null>(null);
	const lastSyncRef = useRef<number>(0);
	const SYNC_INTERVAL = 30 * 60 * 1000; // 30 minutes

	// ----- Helpers -----
	// NEW: processIdToken now accepts optional accessToken and ALWAYS unblocks the UI on valid idToken
	const processIdToken = useCallback((idToken: string, maybeAccessToken?: string | null) => {
		authService.setIdToken(idToken);
		setUserTokenState(idToken);

		const preferredAccess = maybeAccessToken ?? authService.getAccessToken();
		let profile: IUserProfile | null = null;

		if (preferredAccess) {
			profile = authService.extractUserProfileFromAccessToken(preferredAccess);
		}
		if (!profile) {
			profile = authService.extractUserProfileFromIdToken(idToken);
		}

		if (profile) {
			setUserData((prev) => ({
				...prev,
				id: profile.id,
				email: profile.email,
				name: profile.name,
				surname: profile.surname,
				username: profile.username,
				role: profile.role ?? prev.role, // role may fill later when AT arrives
				src: UserImage,
				srcSet: UserImageWebp,
			}));
		}

		// IMPORTANT: mark authenticated once we have a valid ID token
		setIsAuthenticated(true);
		setIsLoading(false); // Ensure loading is set to false
	}, []);

	// ----- /api/auth/sync with single-flight + cache -----
	const syncUserData = useCallback(
		async (force = false) => {
			const now = Date.now();

			if (!force && now - lastSyncRef.current < SYNC_INTERVAL) return null;
			if (syncPromiseRef.current) return syncPromiseRef.current;

			syncPromiseRef.current = (async () => {
				try {
					const user = await authService.syncUser();
					setUserData((prev) => ({ ...prev, id: user.id, email: user.email }));
					lastSyncRef.current = now;
					return user;
				} catch (error) {
					console.error('Error syncing user data:', error);
					// Don't fail the entire auth flow if sync fails
					return null;
				} finally {
					syncPromiseRef.current = null;
				}
			})();

			return syncPromiseRef.current;
		},
		[SYNC_INTERVAL],
	);

	// ----- Token acquisition -----
	const acquireToken = useCallback(async (forceRefresh = false) => {
		if (isRefreshingRef.current && !forceRefresh) {
			await new Promise((r) => setTimeout(r, 100));
			return authService.getAccessToken();
		}

		try {
			isRefreshingRef.current = true;

			const account = msalInstance.getAllAccounts()[0];
			if (!account) throw new Error('No account found');

			const tokenRequest: SilentRequest = {
				scopes: apiScopes,
				account,
				authority: msalConfig.auth.authority,
				forceRefresh,
			};

			const response = await msalInstance.acquireTokenSilent(tokenRequest);
			if (response?.accessToken) {
				authService.setAccessToken(response.accessToken);
				return response.accessToken;
			}
			return null;
		} catch (err) {
			if (err instanceof InteractionRequiredAuthError) {
				try {
					const popupRequest: PopupRequest = {
						scopes: apiScopes,
						authority: msalConfig.auth.authority,
					};
					const response = await msalInstance.acquireTokenPopup(popupRequest);
					if (response?.accessToken) {
						authService.setAccessToken(response.accessToken);
						return response.accessToken;
					}
				} catch {
					await msalInstance.acquireTokenRedirect({
						scopes: apiScopes,
						redirectUri: window.location.origin,
					});
				}
			}
			return null;
		} finally {
			isRefreshingRef.current = false;
		}
	}, []);

	// ----- Init MSAL (one-shot) -----
	useEffect(() => {
		if (initOnceRef.current) return;
		initOnceRef.current = true;

		// Add a timeout to prevent getting stuck
		const initTimeout = setTimeout(() => {
			console.warn('MSAL initialization timeout, forcing completion');
			setIsInitialized(true);
			setIsLoading(false);
		}, 10000); // 10 second timeout

		(async () => {
			try {
				await msalInstance.initialize();
				setIsInitialized(true);

				// Handle redirect response if present
				const response = await msalInstance.handleRedirectPromise();

				// 2) Redirect response path (handle this first if present)
				if (response?.idToken) {
					if (response.accessToken) {
						authService.setAccessToken(response.accessToken);
					}
					processIdToken(response.idToken, response.accessToken);
					if (!response.accessToken) {
						await acquireToken();
					}
					clearTimeout(initTimeout);
					return;
				}

				// 1) Check existing tokens - but be more strict about expiration
				const existingIdToken = authService.getIdToken();
				if (existingIdToken && authService.isTokenValid(existingIdToken, true)) {
					// Use margin for safety
					try {
						const at = await acquireToken(); // prefer to have AT early
						processIdToken(existingIdToken, at ?? undefined);
						clearTimeout(initTimeout);
						return;
					} catch (tokenError) {
						console.warn(
							'Failed to acquire access token with existing ID token:',
							tokenError,
						);
						// If we can't get an access token, the ID token might be stale
						// Clear it and try silent login instead
						authService.clearAuth();
					}
				}

				// 3) Silent login when an account is known (most reliable for returning users)
				const accounts = msalInstance.getAllAccounts();
				if (accounts.length > 0) {
					try {
						console.log('Attempting silent login for existing account...');
						const silent = await msalInstance.acquireTokenSilent({
							scopes: ['openid', 'profile', 'email', ...apiScopes],
							account: accounts[0],
						});
						if (silent.idToken) {
							if (silent.accessToken) {
								authService.setAccessToken(silent.accessToken);
							}
							processIdToken(silent.idToken, silent.accessToken);
							clearTimeout(initTimeout);
							return;
						}
					} catch (e) {
						console.warn('Silent login failed, will require user interaction:', e);
						// Clear any stale tokens and force re-authentication
						authService.clearAuth();
					}
				}

				// If we reach here, no valid tokens found and silent login failed
				// Clear any stale data and require fresh authentication
				authService.clearAuth();
				setIsAuthenticated(false);
				setIsLoading(false);
				clearTimeout(initTimeout);
			} catch (e) {
				console.error('MSAL initialization error:', e);
				// Clear any stale data on error
				authService.clearAuth();
				setIsInitialized(true);
				setIsLoading(false);
				clearTimeout(initTimeout);
			}
		})();
	}, [processIdToken, acquireToken]);

	// ----- Bootstrap (one-shot) after authenticated -----
	useEffect(() => {
		if (!isAuthenticated || isBootstrapped || bootOnceRef.current) return;
		bootOnceRef.current = true;

		// Add a timeout to prevent getting stuck
		const bootstrapTimeout = setTimeout(() => {
			console.warn('Bootstrap timeout, forcing completion');
			setIsBootstrapped(true);
		}, 15000); // 15 second timeout

		(async () => {
			try {
				// Try to sync user data, but don't fail if it doesn't work
				await syncUserData(true).catch(() => {
					console.warn('User sync failed during bootstrap, continuing...');
				});

				// Only proceed with role-specific bootstrap if we have a role
				if (userData.role === UserType.COMPANY) {
					// For company users, we'll let the organization context handle the fetch
					// This prevents race conditions and ensures proper state management
					console.log(
						'Company user detected, organization context will handle data loading',
					);
				} else if (userData.role === UserType.FREELANCE && userData.id) {
					try {
						const profile = await profileService.getFreelanceProfile();
						const profileComplete = !isRichHtmlEmpty(profile?.bioHtml);
						localStorage.setItem(
							'opentalents_profile_completed',
							profileComplete ? 'true' : 'false',
						);
						if (profileComplete) {
							navigate('/freelance/dashboard', { replace: true });
						} else {
							navigate('/freelance/settings', { replace: true });
						}
					} catch (error) {
						console.warn('Profile fetch failed during bootstrap:', error);
						// Don't fail the bootstrap if profile can't be fetched
					}
				}

				setIsBootstrapped(true);
				clearTimeout(bootstrapTimeout);
			} catch (err) {
				console.error('Bootstrap error:', err);
				// Always mark as bootstrapped even if there are errors
				setIsBootstrapped(true);
				clearTimeout(bootstrapTimeout);
			}
		})();
	}, [isAuthenticated, isBootstrapped, userData.role, userData.id, syncUserData, navigate]);

	// ----- Preemptive token refresh -----
	useEffect(() => {
		if (!isAuthenticated || !isInitialized) return;

		const check = async () => {
			const accessToken = authService.getAccessToken();
			if (!accessToken) return;
			const ms = authService.getTimeUntilExpiration(accessToken);
			if (ms > 0 && ms < 5 * 60 * 1000) {
				await acquireToken(true);
			}
		};

		check();
		const id = setInterval(check, 60 * 1000);
		return () => clearInterval(id);
	}, [isAuthenticated, isInitialized, acquireToken]);

	// ----- Global events -----
	useEffect(() => {
		const handleTokenError = () => {
			setIsAuthenticated(false);
			setUserData({});
			navigate('/');
		};

		const handleTokenRefreshNeeded = async () => {
			try {
				console.log('Token refresh needed, attempting to acquire new token...');
				const accessToken = await acquireToken(true);
				if (accessToken) {
					console.log('Token refresh successful, syncing user data...');
					syncUserData(false).catch(() => {});
					// Dispatch event to notify other components that token is refreshed
					window.dispatchEvent(new CustomEvent('auth-token-refreshed'));
				} else {
					console.log('Token refresh failed, no access token received');
					window.dispatchEvent(new CustomEvent('auth-token-error'));
				}
			} catch (error) {
				console.error('Token refresh failed with error:', error);
				window.dispatchEvent(new CustomEvent('auth-token-error'));
			}
		};

		window.addEventListener('auth-token-error', handleTokenError);
		window.addEventListener('auth-token-refresh-needed', handleTokenRefreshNeeded);
		return () => {
			window.removeEventListener('auth-token-error', handleTokenError);
			window.removeEventListener('auth-token-refresh-needed', handleTokenRefreshNeeded);
		};
	}, [navigate, acquireToken, syncUserData]);

	// ----- Public API -----
	const login = useCallback(async () => {
		if (!isInitialized) return;

		try {
			// Check for active interactions
			const hasActive = Object.keys(localStorage).some((k) => k.includes('msal.interaction'));
			if (hasActive) return;

			// Clean up any stale MSAL state first
			Object.keys(localStorage)
				.filter((k) => k.includes('msal.state') || k.includes('msal.interaction'))
				.forEach((k) => localStorage.removeItem(k));

			// Try silent login first for existing accounts
			const accounts = msalInstance.getAllAccounts();
			if (accounts.length > 0) {
				try {
					console.log('Attempting silent login during manual login...');
					const response = await msalInstance.acquireTokenSilent({
						scopes: ['openid', 'profile', 'email', ...apiScopes],
						account: accounts[0],
					});
					if (response.idToken) {
						if (response.accessToken) authService.setAccessToken(response.accessToken);
						processIdToken(response.idToken, response.accessToken);
						return;
					}
				} catch (silentError) {
					console.warn(
						'Silent login failed during manual login, proceeding with redirect:',
						silentError,
					);
					// Clear any stale tokens and force fresh authentication
					authService.clearAuth();
				}
			}

			// Force fresh authentication
			console.log('Initiating fresh authentication...');
			await msalInstance.loginRedirect({
				scopes: ['openid', 'profile', 'email', ...apiScopes],
				redirectStartPage: window.location.href,
				prompt: 'select_account',
			});
		} catch (error: any) {
			if (error?.errorCode !== 'interaction_in_progress') {
				console.error('Login error:', error);
				setIsLoading(false);
				// Don't set authenticated to false here, let the user try again
			}
		}
	}, [isInitialized, processIdToken]);

	const logout = useCallback(() => {
		authService.clearAuth();
		setUserData({});
		setIsAuthenticated(false);
		msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
	}, []);

	// ----- Context value -----
	const value = useMemo(
		() => ({
			userData,
			isAuthenticated,
			isInitialized,
			isLoading,
			isBootstrapped,
			login,
			logout,
			acquireToken,
			// backward compat
			userToken,
			setUserToken: (token: string) => {
				setUserTokenState(token);
				authService.setIdToken(token);
			},
			setUserData,
			syncUserData,
		}),
		[
			userData,
			isAuthenticated,
			isInitialized,
			isLoading,
			isBootstrapped,
			login,
			logout,
			acquireToken,
			userToken,
			setUserData,
			syncUserData,
		],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
